import { useLocationStore as useLocationStoreLegacy } from '@global-store/stores/location'
import { useShopify } from '@internal/beta-features'
import { useLocationStore } from '@shared/global-store/stores/location/location'

import ApiFactory from '@fond-of/api-client'

const client = ApiFactory.new()

type subscriptionState = {
  state: string
}

// can be deleted when when checkout is running on shopify and brands are completly headless
export const languageCodes = {
  de: 'de-DE',
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  da: 'da-DK',
  it: 'it-IT',
}

/**
 * Subscribes a user to the newsletter.
 * @param email The email address of the user.
 * @param source The source from which the user subscribed.
 * @returns A promise that resolves to true if the subscription was successful, false otherwise.
 */
export const subscribeToNewsletter = async (
  email: string,
  source: string,
  lang?: string
): Promise<subscriptionState> => {
  const subscription = {
    state: 'not_subscribed',
  }

  const { languageCode } = useLocationStore()
  const { languageCode: legacyLanguageCode } = useLocationStoreLegacy()

  const isShopify = useShopify(globalThis.location.host)

  // Use provided language for external apps or fallback from storefront locationStore
  let language

  // can be deleted when when checkout is running on shopify and brands are completly headless
  if (lang) {
    language = languageCodes[lang as keyof typeof languageCodes]
  }

  if (!language) {
    language = isShopify ? languageCode : legacyLanguageCode
  }

  try {
    const resp = await client.post('/api/crm/newsletter', {
      json: { email, source, language },
    })

    if (resp.status === 201) {
      subscription.state = 'subscribed'
    }

    if (resp.status === 409) {
      subscription.state = 'already_subscribed'
    }
  } catch {
    return subscription
  }

  return subscription
}

/**
 * Subscribes a user to the "back in stock" notification.
 * @param email The email address of the user.
 * @param productId The ID of the product to subscribe to.
 * @returns A promise that resolves to true if the subscription was successful, false otherwise.
 */
export const subscribeToBackInStock = async (
  email: string,
  productId: string
): Promise<boolean> => {
  const isShopify = useShopify(globalThis.location.host)

  const { languageCode: language } = isShopify
    ? useLocationStore()
    : useLocationStoreLegacy()

  const resp = await client
    .post('/api/crm/back-in-stock', {
      json: { email, productId, language },
    })
    .json<boolean>()

  return resp
}
